<script lang="ts">
  import { useForm, page, Link } from '@inertiajs/svelte';

  interface Props {
    errors?: {
      base?: string[];
      email_address?: string[];
    };
  }

  let { errors: propsErrors }: Props = $props();

  const form = useForm({
    email_address: '',
  });

  let errors = $derived(propsErrors || {});

  function submit(e: Event) {
    e.preventDefault();
    $form.post('/passwords');
  }
</script>

<div class="flex min-h-screen flex-col justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
  <div class="sm:mx-auto sm:w-full sm:max-w-md">
    <!-- Logo/Brand -->
    <div class="text-center">
      <h1 class="text-3xl font-bold text-gray-900">Momentium</h1>
    </div>

    <h2 class="mt-8 text-center text-2xl font-semibold text-gray-900">Reset your password</h2>
    <p class="mx-auto mt-2 max-w-sm text-center text-sm text-gray-600">
      Enter your email address and we'll send you instructions to reset your password.
    </p>
  </div>

  <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
    <div class="border border-gray-100 bg-white px-4 py-8 shadow-lg sm:rounded-xl sm:px-10">
      <!-- Flash messages -->
      {#if $page.props.flash?.notice}
        <div
          class="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700"
        >
          {$page.props.flash.notice}
        </div>
      {/if}

      {#if $page.props.flash?.alert}
        <div
          class="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-700"
        >
          {$page.props.flash.alert}
        </div>
      {/if}

      <!-- Base errors (rate limiting) -->
      {#if errors?.base}
        <div class="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {#each errors.base as error}
            <p>{error}</p>
          {/each}
        </div>
      {/if}

      <form onsubmit={submit} class="space-y-6">
        <!-- Email -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700"> Email address </label>
          <input
            type="email"
            id="email"
            name="email_address"
            autocomplete="email"
            bind:value={$form.email_address}
            class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            required
          />
          {#if errors?.email_address}
            <p class="mt-1 text-sm text-red-600">{errors.email_address.join(', ')}</p>
          {/if}
        </div>

        <!-- Submit button -->
        <button
          type="submit"
          disabled={$form.processing}
          class="flex w-full justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {#if $form.processing}
            <svg
              class="mr-2 -ml-1 h-4 w-4 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Sending...
          {:else}
            Send reset instructions
          {/if}
        </button>
      </form>

      <!-- Back to login link -->
      <div class="mt-6 text-center">
        <Link href="/login" class="text-sm text-blue-600 hover:text-blue-500 hover:underline">
          ← Back to sign in
        </Link>
      </div>
    </div>
  </div>
</div>
